import React from 'react';

import { Typography, Tooltip, Tag } from 'antd';

import {
  MENU_OBJECT_TYPES,
  CHOWNOW_TYPE_TO_READABLE_STRING_MAP,
  FIELD_INPUT_TYPES,
} from 'constants/menuManagement';

import { SortIcon } from 'helpers/table';

import ConditionalSoldOutRibbon from 'components/MenuManagement/ConditionalSoldOutRibbon';
import RecordEnabledSwitch from 'components/MenuManagement/Fields/RecordEnabledSwitch';
import DeleteMenuItemButton from 'components/MenuManagement/MenuItemActions/DeleteMenuItemButton';

import { RenderActions } from './MenuItemActions/RenderActions';

const { Text } = Typography;

// Column Configurations
const sortColumn = {
  title: 'ORDER',
  dataIndex: 'ordinal',
  key: 'ordinal',
  editable: true,
  inputType: 'number',
  defaultSortOrder: 'ascend',
  width: '10px',
  sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
  showSorterTooltip: false,
  sorter: (a, b) => {
    if (a.ordinal - b.ordinal === 0) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    }
    return a.ordinal - b.ordinal;
  },
  render: (_, record) => {
    if (!record) return null;
    if (record.itemType === MENU_OBJECT_TYPES.modifiers) {
      return (
        <Tooltip title={record.returnDateMessage}>
          <ConditionalSoldOutRibbon isSoldOut={record.paused} />
          <Text>{record.ordinal}</Text>
        </Tooltip>
      );
    }
    return <Text>{record.ordinal}</Text>;
  },
};

const typeColumn = {
  title: 'TYPE',
  dataIndex: 'itemType',
  key: 'itemType',
  editable: false,
  width: '10px',
  render: (_, record) => (
    <Tag className="chownow-tag--default" data-cy={`item-type-tag-${record?.uuid}`}>
      {`${record?.isNested ? 'Nested ' : ''}${
        CHOWNOW_TYPE_TO_READABLE_STRING_MAP[record?.itemType]
      }`}
    </Tag>
  ),
};

const nameColumn = {
  title: 'Name',
  dataIndex: 'name',
  key: 'name',
  editable: true,
};

const descriptionColumn = {
  title: 'Description',
  dataIndex: 'description',
  key: 'description',
  editable: true,
};

const posIDField = {
  title: 'POS ID',
  dataIndex: 'externalId',
  key: 'posId',
  inputType: 'posId',
  editable: false,
};

const aliasNameField = {
  title: 'Alias name',
  dataIndex: 'internalName',
  key: 'aliasName',
  inputType: 'aliasName',
};

export const menuDrawerFieldConfig = {
  sort: sortColumn,
  name: nameColumn,
  description: descriptionColumn,
  menuHours: {
    title: 'Menu Availability',
    dataIndex: 'itemHours',
    key: 'itemHours',
    inputType: 'hours',
  },
  integrations: {
    title: 'Integrations',
    dataIndex: 'integrations',
    key: 'integrations',
    inputType: 'integrationSelector',
  },
  merchants: {
    title: 'Linked Merchants',
    dataIndex: 'merchants',
    key: 'merchants',
    inputType: 'merchantSelector',
  },
  itemID: {
    title: 'Menu ID',
    dataIndex: 'itemId',
    editable: false,
    inputType: 'itemId',
  },
  taxRate: {
    title: 'Default Tax Rate (%)',
    dataIndex: 'taxRate',
    key: 'taxRate',
    inputType: 'number',
  },
  specialInstructionsEnabled: {
    title: 'Enable Special Instructions',
    dataIndex: 'specialInstructionsEnabled',
    key: 'specialInstructionsEnabled',
    inputType: 'switch',
  },
  action: {
    title: 'Action',
    render: menu => <DeleteMenuItemButton record={menu} group={MENU_OBJECT_TYPES.categories} />,
  },
};

const categoryDrawerConfig = {
  name: { ...nameColumn, title: 'Category name' },
  description: descriptionColumn,
  taxRate: {
    title: 'Category tax rate',
    dataIndex: 'taxRate',
    key: 'taxRate',
    inputType: 'customTaxComboBox',
  },
  items: {
    title: 'Items',
    dataIndex: 'children',
    key: 'items',
    inputType: 'childSelector',
  },
  itemID: {
    title: 'Category ID',
    dataIndex: 'itemId',
    editable: false,
    inputType: 'itemId',
  },
  posID: posIDField,
};

const mainItemDrawerFieldConfig = {
  name: { ...nameColumn, title: 'Item name' },
  description: descriptionColumn,
  category: {
    title: 'Category *',
    dataIndex: 'parents',
    key: 'categories',
    inputType: 'parentSelector',
  },
  price: {
    title: 'Price',
    dataIndex: 'price',
    key: 'price',
    editable: false,
    inputType: 'price',
  },
  secondaryPrice: {
    title: 'Pick-Up Price',
    dataIndex: 'secondaryPrice',
    key: 'secondaryPrice',
    editable: false,
    inputType: 'price',
  },
  modifierGroup: {
    title: 'Modifier groups',
    dataIndex: 'children',
    key: 'modifierGroups',
    inputType: 'childSelector',
  },
  imageUrl: {
    title: 'Image URL',
    dataIndex: 'imageUrl',
    key: 'imageAddress',
    editable: true,
  },
  uploadImage: {
    title: '',
    dataIndex: 'itemPhoto',
    editable: false,
    inputType: 'upload',
  },
  itemID: {
    title: 'Item ID',
    dataIndex: 'itemId',
    editable: false,
    inputType: 'itemId',
  },
  posID: posIDField,
  aliasName: aliasNameField,
  classifications: {
    title: 'Class',
    dataIndex: 'classifications',
    key: 'classifications',
    inputType: 'classifications',
  },
  servingsTag: {
    title: 'Servings',
    dataIndex: 'servingsTag',
    key: 'servingsTag',
    inputType: FIELD_INPUT_TYPES.NUMBER_RANGE_EXTRA_DETAIL,
  },
  taxRate: {
    title: 'Item tax rate',
    dataIndex: 'taxRate',
    key: 'taxRate',
    inputType: 'customTaxComboBox',
  },
};

const modifierGroupsColumnConfig = {
  itemType: typeColumn,
  sort: sortColumn,
  name: {
    title: 'Group Name',
    dataIndex: 'name',
    key: 'modifierGroupName',
    editable: true,
  },
  description: descriptionColumn,
  min: {
    editable: true,
    title: 'Minimum',
    dataIndex: 'minimum',
    key: 'min',
    inputType: 'number',
    width: '10px',
  },
  max: {
    editable: true,
    title: 'Maximum',
    dataIndex: 'maximum',
    key: 'max',
    inputType: 'number',
    width: '10px',
  },
  enabled: {
    title: 'Enable',
    dataIndex: 'enabled',
    key: 'enabled',
    render: (_, record) => <RecordEnabledSwitch record={record} />,
    width: '10px',
  },
  action: {
    title: '',
    key: 'action',
    render: record => (
      <RenderActions record={record} group={MENU_OBJECT_TYPES.modifierGroups} showEdit />
    ),
  },
};

const modifierGroupsDrawerConfig = {
  name: { ...nameColumn, title: 'Group name' },
  description: descriptionColumn,
  min: {
    title: 'Minimum',
    dataIndex: 'minimum',
    key: 'min',
    editable: true,
    inputType: 'number',
  },
  max: {
    title: 'Maximum',
    dataIndex: 'maximum',
    key: 'max',
    editable: true,
    inputType: 'number',
  },
  items: menu => ({
    title: menu.isNested ? 'Parent Modifiers *' : 'Items *',
    dataIndex: 'parents',
    key: 'items',
    inputType: 'parentSelector',
  }),
  modifier: menu => ({
    title: menu.isNested ? 'Nested Modifiers *' : 'Modifiers *',
    dataIndex: 'children',
    key: 'modifiers',
    inputType: 'childSelector',
  }),
  itemID: {
    title: 'Modifier group ID',
    dataIndex: 'itemId',
    editable: false,
    inputType: 'itemId',
  },
  allowMultipleModifiers: {
    title: 'Allow Multiple Individual Modifiers',
    dataIndex: 'allowMultipleModifiers',
    editable: true,
    inputType: 'allowMultipleModifiersCheckbox',
  },
  posID: posIDField,
};

const parentDrawerConfig = {
  parent: {
    key: 'parent',
    inputType: 'parent',
  },
};

const modifierDrawerConfig = {
  sort: sortColumn,
  name: {
    title: 'Modifier name',
    dataIndex: 'name',
    key: 'name',
    editable: true,
  },
  description: descriptionColumn,
  price: {
    title: 'Price',
    dataIndex: 'price',
    key: 'price',
    inputType: 'price',
  },
  secondaryPrice: {
    title: 'Pickup price',
    dataIndex: 'secondaryPrice',
    key: 'secondaryPrice',
    editable: false,
    inputType: 'price',
  },
  posID: posIDField,
  enabled: {
    title: 'Enabled',
    dataIndex: 'enabled',
    key: 'enabled',
    render: (_, record) => <RecordEnabledSwitch record={record} />,
    width: '10px',
  },
  modifierGroups: {
    title: 'Modifier group *',
    dataIndex: 'parents',
    key: 'modifierGroups',
    inputType: 'parentSelector',
  },
  nestedModifierGroups: {
    title: 'Nested modifier group',
    dataIndex: 'children',
    key: 'nestedModifierGroups',
    inputType: 'childSelector',
  },
  classifications: {
    title: 'Class',
    dataIndex: 'classifications',
    key: 'classifications',
    inputType: 'classifications',
  },
  isDefault: {
    title: 'Default',
    dataIndex: 'isDefault',
    key: 'isDefault',
    text: 'Default',
    inputType: FIELD_INPUT_TYPES.DROPDOWN_SELECTOR,
    editable: false,
  },
  itemID: {
    title: 'Modifier ID',
    dataIndex: 'itemId',
    editable: false,
    inputType: 'itemId',
  },
};

// Drawer - Edit Fields
export const menuManagementMenuDrawerFields = () => [
  menuDrawerFieldConfig.name,
  menuDrawerFieldConfig.description,
  menuDrawerFieldConfig.menuHours,
  menuDrawerFieldConfig.specialInstructionsEnabled,
  menuDrawerFieldConfig.integrations,
  menuDrawerFieldConfig.taxRate,
  menuDrawerFieldConfig.merchants,
  menuDrawerFieldConfig.itemID,
];

export const menuManagementCategoryDrawerFields = () => [
  categoryDrawerConfig.name,
  categoryDrawerConfig.description,
  categoryDrawerConfig.items,
  categoryDrawerConfig.taxRate,
  categoryDrawerConfig.posID,
  categoryDrawerConfig.itemID,
];

const menuManagementMainItemDrawerFields = () => [
  mainItemDrawerFieldConfig.name,
  mainItemDrawerFieldConfig.imageUrl,
  mainItemDrawerFieldConfig.uploadImage,
  mainItemDrawerFieldConfig.description,
  mainItemDrawerFieldConfig.category,
  mainItemDrawerFieldConfig.price,
  mainItemDrawerFieldConfig.secondaryPrice,
  mainItemDrawerFieldConfig.modifierGroup,
  mainItemDrawerFieldConfig.servingsTag,
  mainItemDrawerFieldConfig.classifications,
  mainItemDrawerFieldConfig.taxRate,
  mainItemDrawerFieldConfig.posID,
  mainItemDrawerFieldConfig.aliasName,
  mainItemDrawerFieldConfig.itemID,
];

const menuManagementModifierGroupsEditFields = record => [
  modifierGroupsDrawerConfig.name,
  modifierGroupsDrawerConfig.description,
  modifierGroupsDrawerConfig.min,
  modifierGroupsDrawerConfig.max,
  modifierGroupsDrawerConfig.items(record),
  modifierGroupsDrawerConfig.modifier(record),
  modifierGroupsDrawerConfig.allowMultipleModifiers,
  modifierGroupsDrawerConfig.posID,
  modifierGroupsDrawerConfig.itemID,
];

const menuManagementModifierEditFields = ({ nestedModGroupEnabled = false } = {}) => [
  modifierDrawerConfig.name,
  modifierDrawerConfig.description,
  modifierDrawerConfig.price,
  modifierDrawerConfig.secondaryPrice,
  modifierDrawerConfig.modifierGroups,
  // Assigning a child mod group to a modifier is gated behind a feature flag AND the BE
  // confirming this modifier is nest-eligible (see nestedModGroupEnabled in EditDrawer).
  ...(nestedModGroupEnabled ? [modifierDrawerConfig.nestedModifierGroups] : []),
  modifierDrawerConfig.classifications,
  modifierDrawerConfig.isDefault,
  modifierDrawerConfig.posID,
  modifierDrawerConfig.itemID,
];

const menuManagementCategoryAddFields = [
  parentDrawerConfig.parent,
  categoryDrawerConfig.name,
  categoryDrawerConfig.description,
];

const menuManagementMainItemAddFields = [
  parentDrawerConfig.parent,
  mainItemDrawerFieldConfig.name,
  mainItemDrawerFieldConfig.imageUrl,
  mainItemDrawerFieldConfig.uploadImage,
  mainItemDrawerFieldConfig.description,
  mainItemDrawerFieldConfig.price,
  mainItemDrawerFieldConfig.servingsTag,
  mainItemDrawerFieldConfig.classifications,
];

const menuManagementModifierGroupAddFields = [
  parentDrawerConfig.parent,
  modifierGroupsColumnConfig.name,
  modifierGroupsColumnConfig.description,
  modifierGroupsColumnConfig.min,
  modifierGroupsColumnConfig.max,
  modifierGroupsDrawerConfig.allowMultipleModifiers,
];

const menuManagementModifierAddFields = [
  parentDrawerConfig.parent,
  modifierDrawerConfig.name,
  modifierDrawerConfig.description,
  modifierDrawerConfig.price,
  modifierDrawerConfig.classifications,
  modifierDrawerConfig.isDefault,
];

// Generalized Exports
export const drawerAddConfig = {
  [MENU_OBJECT_TYPES.categories]: menuManagementCategoryAddFields,
  [MENU_OBJECT_TYPES.items]: menuManagementMainItemAddFields,
  [MENU_OBJECT_TYPES.modifierGroups]: menuManagementModifierGroupAddFields,
  [MENU_OBJECT_TYPES.modifiers]: menuManagementModifierAddFields,
};

export const drawerEditConfig = (record, flags = {}) => ({
  [MENU_OBJECT_TYPES.menus]: menuManagementMenuDrawerFields(),
  [MENU_OBJECT_TYPES.categories]: menuManagementCategoryDrawerFields(),
  [MENU_OBJECT_TYPES.items]: menuManagementMainItemDrawerFields(),
  [MENU_OBJECT_TYPES.modifierGroups]: menuManagementModifierGroupsEditFields(record),
  [MENU_OBJECT_TYPES.modifiers]: menuManagementModifierEditFields({
    nestedModGroupEnabled: flags.nestedModGroupEnabled,
  }),
});
